import type { Product, ProductForCustomer } from "../../../core/schema/Product.js";
import { comm } from "../../../core/utils/list/comm.js";

export interface ProductForCrossSale extends ProductForCustomer, Pick<Product, "isCrossSale"> {
	/** Určuje pořadí mezi nabídkami, čím vyšší, tím dřív se zobrazí. Název atributu je záměrně obecný. */
	priority: number;
}

export interface CrossSaleProducts {
	backups: ProductForCustomer[];
	desserts: ProductForCrossSale[];
	extras: ProductForCrossSale[];
	forEvents12: Record<string, ProductForCrossSale[]>;
	forEvents3: Record<string, ProductForCrossSale[]>;
	others: ProductForCrossSale[];
}

function intersection(left: string[], right: string[]): string[] {
	return comm(left, right)[1];
}

function combineSorters<T>(sorters: ((a: T, b: T) => number)[]) {
	return function (a: T, b: T): number {
		for (const sorter of sorters) {
			const res = sorter(a, b);
			if (res !== 0) {
				return res;
			}
		}
		return 0;
	};
}

function prefer(preferredCrossSaleProductIds: Set<string>) {
	return function (a: ProductForCrossSale, b: ProductForCrossSale): number {
		const aPreferred = a.isCrossSale && preferredCrossSaleProductIds.has(a.id);
		const bPreferred = b.isCrossSale && preferredCrossSaleProductIds.has(b.id);
		if (aPreferred === bPreferred) {
			return 0;
		}
		return bPreferred ? +1 : -1;
	};
}

function crossSaleFirst(a: ProductForCrossSale, b: ProductForCrossSale): number {
	const aCrossSale = a.isCrossSale ? 1 : 0;
	const bCrossSale = b.isCrossSale ? 1 : 0;
	return bCrossSale - aCrossSale;
}

function prioritize(a: ProductForCrossSale, b: ProductForCrossSale): number {
	return b.priority - a.priority;
}

function except(excludeIds: string[]) {
	return function (product: ProductForCustomer): boolean {
		return !excludeIds.includes(product.id);
	};
}

function clean(product: ProductForCrossSale): ProductForCustomer {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { isCrossSale, priority, ...rest } = product;
	return rest;
}

export function getCrossSaleFor(
	crossSaleProducts: CrossSaleProducts,
	cartProductIds: string[],
	forProducts: Pick<ProductForCustomer, "categoryIds" | "preferredCrossSaleProductIds">[],
): ProductForCustomer[] {
	/** Speciální kategorie, pro které budeme hledat nabídky. */
	const forEventCategoryIds = new Set<string>();
	/** Produkty, které budeme v nabídkách preferovat. */
	const preferredCrossSaleProductIds = new Set<string>();
	/** Budeme hledat i nabídky pro nespeciální kategorie? */
	let forNormalCategories = false;

	const eventCategoryIds = Object.keys(crossSaleProducts.forEvents12);
	for (const forProduct of forProducts) {
		const eventCategoryIdsOfProduct = intersection(forProduct.categoryIds, eventCategoryIds);
		for (const eventCategoryId of eventCategoryIdsOfProduct) {
			forEventCategoryIds.add(eventCategoryId);
		}
		for (const productId of forProduct.preferredCrossSaleProductIds) {
			preferredCrossSaleProductIds.add(productId);
		}
		if (eventCategoryIdsOfProduct.length === 0) {
			forNormalCategories = true;
		}
	}

	const candidates1: ProductForCrossSale[] = [];
	const candidates2: ProductForCrossSale[] = [];
	const candidates3: ProductForCrossSale[] = [];

	for (const categoryId of forEventCategoryIds) {
		if (!crossSaleProducts.forEvents12[categoryId] || !crossSaleProducts.forEvents3[categoryId]) {
			throw new Error("Missing event category ID in forEvents");
		}
		candidates1.push(...crossSaleProducts.forEvents12[categoryId]);
		candidates2.push(...crossSaleProducts.forEvents12[categoryId]);
		candidates3.push(...crossSaleProducts.forEvents3[categoryId]);
	}
	if (forNormalCategories) {
		candidates1.push(...crossSaleProducts.extras);
		candidates2.push(...crossSaleProducts.desserts);
		candidates3.push(...crossSaleProducts.others);
	}

	const slot12sorter = combineSorters([prefer(preferredCrossSaleProductIds), crossSaleFirst, prioritize]);
	candidates1.sort(slot12sorter);
	candidates2.sort(slot12sorter);
	candidates3.sort(prioritize);

	const [slot1] = candidates1.map(clean).filter(except(cartProductIds)).concat(crossSaleProducts.backups);
	const [slot2] = candidates2
		.map(clean)
		.filter(except(cartProductIds))
		.concat(crossSaleProducts.backups)
		.filter(except([slot1?.id ?? ""]));
	const [slot3] = candidates3
		.map(clean)
		.filter(except(cartProductIds))
		.concat(crossSaleProducts.backups)
		.filter(except([slot1?.id ?? "", slot2?.id ?? ""]));
	if (!slot1 || !slot2 || !slot3) {
		// this shouldn't really happen unless the whole e-shop has less than 3 products available for sale
		return [];
	}
	return [slot1, slot2, slot3];
}
